import { useEffect, useRef, useState } from 'react';

import { Grid, LinearProgress, Typography } from '@material-ui/core';
import { QEScoreSummaryCard } from './components/QEScoreSummary';
import { QEScoreTrend } from './components/QEScoreTrend';
import { QEScoreBreakdown } from './components/QEScoreBreakdown';
import { QEScoreAnalysis } from './components/QEScoreAnalysis';
import { useQeScore } from '../../hooks/useQeScore';
import { useParams } from 'react-router';
import { useStyles } from './styles';
import { QeScoreAnalysisStatus } from './interfaces';
import { NotApplicable } from './components/NotApplicable';
import { QeScoreLoadError } from './components/QeScoreLoadError';
import { ANALYSIS_EXECUTION_STATUS, TABS } from './constants';
import {
  getMaximumKpiWeight,
  hasNoScoreData,
  isAppNotApplicable,
  scrollToBottom,
} from './utils';
import WarningTriangleIcon from '../../icons/WarningTriangleIcon';

function shouldDisableByAnalysisStatus(
  status?: QeScoreAnalysisStatus,
): boolean {
  if (!status) {
    return false;
  }
  return (
    status.toUpperCase() === ANALYSIS_EXECUTION_STATUS.DONE ||
    status.toUpperCase() === ANALYSIS_EXECUTION_STATUS.IN_PROGRESS
  );
}

interface QEScorePageProps {
  isToolAdmin: boolean;
  applicationName?: string;
}
export default function QEScorePage({
  isToolAdmin,
  applicationName,
}: QEScorePageProps) {
  const classes = useStyles();
  const { projectId } = useParams();

  // auto-resume an in-progress analysis session.
  const resumeAttemptedRef = useRef(false);

  // Prevents API "in progress" fallback UI from reappearing right after we successfully load results.
  const analysisJustCompletedRef = useRef(false);

  const [activeTab, setActiveTab] = useState<(typeof TABS)[keyof typeof TABS]>(
    TABS.WORK_LOG,
  );

  // True while the page owns an active analysis run lifecycle (trigger/resume/awaiting results).
  const [analysisSessionActive, setAnalysisSessionActive] = useState(false);

  const {
    qeScoreSummary,
    qeScoreAnalysisResults,
    isQeScoreSummaryLoading,
    isStreamingLoading,
    isTriggering,
    isAnalysisLoading,
    isRefreshingAnalysisStatus,
    isDownloadingReport,
    error,
    errorType,
    fetchQeScoreAnalysisResults,
    fetchQeScoreAnalysisReport,
    fetchQeScoreAnalysisStatus,
    streamQeScoreWorkLogs,
  } = useQeScore(projectId!);

  const isApiAnalysisInProgress =
    qeScoreSummary?.analysisDoneToday?.toUpperCase() ===
    ANALYSIS_EXECUTION_STATUS.IN_PROGRESS;

  const showInProgressFromApi =
    isApiAnalysisInProgress &&
    !analysisSessionActive &&
    !isStreamingLoading &&
    !analysisJustCompletedRef.current;

  // Unified streaming state for UI (current session stream OR  API in-progress).
  const effectiveIsStreamingLoading =
    isStreamingLoading || showInProgressFromApi;

  // Unified trigger/loading state for busy cursor and top-level analysis progress.
  const effectiveIsTriggering = isTriggering || showInProgressFromApi;

  const hasExistingAnalysis = qeScoreAnalysisResults !== null;

  //  display results when previous analysis exists and no active session or streaming
  const showAnalysisResults =
    hasExistingAnalysis && !analysisSessionActive && !isStreamingLoading;

  //  Show analysis container during run, or when final results are ready.
  const showAnalysisPanel =
    effectiveIsStreamingLoading || showAnalysisResults || analysisSessionActive;

  useEffect(() => {
    if (showInProgressFromApi) {
      setActiveTab(TABS.WORK_LOG);
      return;
    }

    if (showAnalysisResults) {
      setActiveTab(TABS.ANALYSIS_RESULTS);
    }
  }, [showAnalysisResults, showInProgressFromApi]);

  // If user refreshes or re-navigates to the page while analysis is in progress,
  // resume streaming logs, without re-triggering analysis
  useEffect(() => {
    if (!isApiAnalysisInProgress) {
      resumeAttemptedRef.current = false;
      return;
    }

    if (
      isStreamingLoading ||
      analysisSessionActive ||
      resumeAttemptedRef.current
    ) {
      return;
    }

    const resumeSession = async () => {
      resumeAttemptedRef.current = true;
      setActiveTab(TABS.WORK_LOG);
      setAnalysisSessionActive(true);

      // Resume streaming logs without re-triggering analysis
      await initiateAnalysis({ isResuming: true });
    };

    resumeSession();
  }, [
    qeScoreSummary?.analysisDoneToday,
    isStreamingLoading,
    analysisSessionActive,
  ]);

  const initiateAnalysis = async ({ isResuming }: { isResuming: boolean }) => {
    try {
      // when analysis starts, show Worklog tab and stream logs
      await streamQeScoreWorkLogs({
        triggerAnalysis: !isResuming,
        resetLogs: !isResuming,
      });

      // streaming is done, switch to analysis results tab
      setActiveTab(TABS.ANALYSIS_RESULTS);

      await refreshAnalysisResults();
      scrollToBottom();
    } catch (err) {
      if (isResuming) resumeAttemptedRef.current = false;
    } finally {
      // Check the status of the analysis at the end of session
      // if successful, button is disabled
      // if failed, button is enabled again to allow retry
      await fetchQeScoreAnalysisStatus();
      setAnalysisSessionActive(false);
    }
  };

  const handleAnalysisResultsTabClick = () => {
    // if analysis is in progress, do not switch tabs
    // tab switches automatically after streaming is done
    if (effectiveIsStreamingLoading || isRefreshingAnalysisStatus) {
      return;
    }

    setActiveTab(TABS.ANALYSIS_RESULTS);
    scrollToBottom();
  };

  const handleWorkLogTabClick = () => {
    setActiveTab(TABS.WORK_LOG);
    scrollToBottom();
  };

  const handleAnalyzeScore = async () => {
    analysisJustCompletedRef.current = false;
    setAnalysisSessionActive(true);

    setActiveTab(TABS.WORK_LOG);
    scrollToBottom();

    await initiateAnalysis({ isResuming: false });
  };

  const refreshAnalysisResults = async () => {
    try {
      const response = await fetchQeScoreAnalysisResults(true);
      if (response) {
        analysisJustCompletedRef.current = true;
      }
    } catch {
      // Results not ready yet
    }
  };

  const isZeroScore = hasNoScoreData(
    qeScoreSummary?.trendData ?? [],
    qeScoreSummary?.currentMonthWeight,
  );

  // Keep button disabled while actively processing and when analysis status is DONE/IN_PROGRESS
  const isButtonDisabled =
    effectiveIsStreamingLoading ||
    isAnalysisLoading ||
    isRefreshingAnalysisStatus ||
    analysisSessionActive ||
    shouldDisableByAnalysisStatus(qeScoreSummary?.analysisDoneToday) ||
    isZeroScore;

  const isAnalysisInProgress =
    effectiveIsTriggering || effectiveIsStreamingLoading;

  function renderPageContent() {
    if (isQeScoreSummaryLoading) {
      return (
        <Grid container>
          <Grid item xs={12}>
            <LinearProgress />
          </Grid>
          <Grid item xs={12}>
            <Typography className={classes.indexPageLoadStatusMessage}>
              Fetching QE Score data...
            </Typography>
          </Grid>
        </Grid>
      );
    }
    if (error) {
      return <QeScoreLoadError errorType={errorType} />;
    }

    if (
      qeScoreSummary &&
      isAppNotApplicable(qeScoreSummary?.kpiBreakdown ?? [])
    ) {
      return <NotApplicable />;
    }
    return (
      <div className={classes.indexPageContent}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Grid container direction="column" spacing={3}>
              <Grid item>
                <QEScoreSummaryCard
                  applicationName={applicationName!}
                  isLoading={
                    isQeScoreSummaryLoading ||
                    effectiveIsStreamingLoading ||
                    isAnalysisLoading
                  }
                  isDisabled={
                    !isToolAdmin ||
                    isButtonDisabled ||
                    isQeScoreSummaryLoading ||
                    effectiveIsStreamingLoading
                  }
                  onAnalyzeClick={handleAnalyzeScore}
                  currentMonthWeight={
                    qeScoreSummary?.currentMonthWeight.maxWeight ?? 0
                  }
                  maximumKpiWeightage={getMaximumKpiWeight(
                    qeScoreSummary?.kpiBreakdown,
                  )}
                  analysisDoneToday={qeScoreSummary?.analysisDoneToday}
                />
              </Grid>
              <Grid item>
                <QEScoreTrend trendData={qeScoreSummary?.trendData ?? []} />
              </Grid>
            </Grid>
          </Grid>

          {/* Right Column - QE Score Breakdown (Mobile: full width, Tablet+: 1/2) */}
          <Grid item xs={12} md={6}>
            <QEScoreBreakdown metrics={qeScoreSummary?.kpiBreakdown ?? []} />
          </Grid>
        </Grid>

        {isZeroScore ? (
          <div className={classes.notApplicableRoot}>
            <div className={classes.notApplicableIconWrapper}>
              <WarningTriangleIcon size="large" />
            </div>
            <Typography variant="h6" className={classes.notApplicableTitle}>
              No data is available to analyze yet.
            </Typography>

            <div className={classes.notApplicableDivider} />

            <Typography variant="body2" className={classes.notApplicableBody}>
              Start adding user stories to your application to generate a QE
              Score.
            </Typography>
          </div>
        ) : (
          <QEScoreAnalysis
            isStreamingLoading={effectiveIsStreamingLoading}
            isTriggering={effectiveIsTriggering}
            isAnalysisLoading={isAnalysisLoading}
            showAnalysisPanel={showAnalysisPanel}
            activeTab={activeTab}
            onAnalysisResultsTabClick={handleAnalysisResultsTabClick}
            onWorkLogTabClick={handleWorkLogTabClick}
            showAnalysisResults={showAnalysisResults}
            analysisResults={qeScoreAnalysisResults ?? null}
            isDownloadingReport={isDownloadingReport}
            onDownloadReportClick={fetchQeScoreAnalysisReport}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={`${classes.qeThemeScope} ${
        isAnalysisInProgress ? classes.indexBusyCursor : ''
      }`}
    >
      {renderPageContent()}
    </div>
  );
}
